import * as React from 'react'

import { AsyncSelect } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { useProcurifyIntl } from '@webapp/hooks'
import { slackIntegrationRepository } from '@webapp/platform/integrations/data-access/repositories/SlackIntegrationRepository'
import { SlackUser } from '@webapp/platform/integrations/entities/SlackUser'
import messages from './messages'

export interface ISlackUserAsyncSelectProps {
  onSelect: (slackUser: SlackUser) => void
}

export const SlackUserAsyncSelect = ({
  onSelect,
}: ISlackUserAsyncSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions = async (
    inputValue: string,
    pagination: { page: number },
    callback: (result: {
      options: SlackUser[]
      pagination: { hasMore: boolean; page: number }
    }) => void
  ) => {
    try {
      const res = await slackIntegrationRepository.listSlackUsers({
        collection: 'non_bots',
        orderBy: 'name',
        search: inputValue,
        page: pagination.page,
        limit: 20,
      })

      callback({
        options: res.data.map((user) => new SlackUser(user)),
        pagination: {
          hasMore: res.pagination
            ? res.pagination.page < res.pagination.numPages
            : false,
          page: pagination.page + 1,
        },
      })
    } catch {
      toast.error({
        title: intl.formatMessage(messages.loadErrorTitle),
        children: intl.formatMessage(messages.loadErrorMessage),
      })
      callback({ options: [], pagination: { hasMore: false, page: 1 } })
    }
  }

  return (
    <AsyncSelect<SlackUser>
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholder)}
      getOptionLabel={(option) => option.displayName}
      getOptionValue={(option) => option.id}
      onChange={(option) => {
        if (option) {
          onSelect(option)
        }
      }}
    />
  )
}

SlackUserAsyncSelect.displayName = 'SlackUserAsyncSelect'
